// OrgSettingsPanels.js — the shared, presentational guts of the Settings dialog.
//
// These panels are rendered in two places and must stay identical in both:
//   1. settings.js          — for the organisation the user is logged in as
//   2. SiteManagement.js    — for ANY organisation, driven by an admin
//
// They are deliberately dumb: everything comes in through props and every edit
// goes back out through one onChange. Neither panel fetches or saves — the
// host decides where the values come from and where they go, which is the only
// real difference between the two callers.

import React from "react";
import {
  Button,
  Input,
  InputLabel,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";

export const defaultOpeningTimes = {
  Monday: { open: "09:00", close: "18:00" },
  Tuesday: { open: "09:00", close: "18:00" },
  Wednesday: { open: "09:00", close: "18:00" },
  Thursday: { open: "09:00", close: "18:00" },
  Friday: { open: "09:00", close: "18:00" },
  Saturday: { open: "09:00", close: "13:00" },
  Sunday: { open: "00:00", close: "00:00" },
};

// Default patient app settings
export const defaultPatientAppSettings = {
  allow_messaging: true,
  allow_replies: false,
  allow_booking: false,
  repeats: false,
  medication_reminders: false,
  show_phone_number: true,
  primary_hex: '#2D5A3D',
  shop_enabled: false,
  shop_id: '',
  pharmacy_phone_number: '',
  pharmacy_phone_lines: [], // Array of { label, number }
  buttons: [], // Array of { label, url }
  top_box_text: ''
};

export const PatientAppSection = ({ settings, onUpdate, onInherit }) => {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Patient App Settings</h4>
        <Button
          variant="outlined"
          size="small"
          onClick={onInherit}
        >
          Inherit from organisation
        </Button>
      </div>

      {/* Feature toggles */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Features</h5>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.allow_messaging || false}
                onChange={(e) => onUpdate('allow_messaging', e.target.checked)}
              />
            }
            label="Enable message view"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.allow_replies || false}
                onChange={(e) => onUpdate('allow_replies', e.target.checked)}
              />
            }
            label="Allow message replies"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.allow_booking || false}
                onChange={(e) => onUpdate('allow_booking', e.target.checked)}
              />
            }
            label="Enable clinic booking"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.repeats || false}
                onChange={(e) => onUpdate('repeats', e.target.checked)}
              />
            }
            label="Allow repeat ordering"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.medication_reminders || false}
                onChange={(e) => onUpdate('medication_reminders', e.target.checked)}
              />
            }
            label="Show medication reminders"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.show_phone_number !== false}
                onChange={(e) => onUpdate('show_phone_number', e.target.checked)}
              />
            }
            label="Show pharmacy phone number"
          />
        </div>
      </div>

      {/* Theme */}
      <div style={{ marginBottom: 12 }}>
        <InputLabel>Theme (Primary Color)</InputLabel>
        <input
          type="color"
          value={settings.primary_hex || '#2D5A3D'}
          onChange={(e) => onUpdate('primary_hex', e.target.value)}
          style={{ width: 60, height: 40, borderRadius: 4, cursor: "pointer" }}
        />
      </div>

      {/* Contact Information */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Contact Information</h5>
        
        <InputLabel>Main pharmacy phone number</InputLabel>
        <Input
          type="tel"
          value={settings.pharmacy_phone_number || ''}
          onChange={(e) => onUpdate('pharmacy_phone_number', e.target.value)}
          placeholder="07540876708"
          fullWidth
          style={{ marginBottom: 12 }}
        />

        <h6 style={{ marginBottom: 8 }}>Phone lines (up to 3)</h6>
        {[0, 1, 2].map((idx) => {
          const line = (settings.pharmacy_phone_lines || [])[idx] || { label: '', number: '' };
          return (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <TextField
                label="Label"
                size="small"
                value={line.label || ''}
                onChange={(e) => {
                  const updated = [...(settings.pharmacy_phone_lines || [])];
                  updated[idx] = { ...line, label: e.target.value };
                  onUpdate('pharmacy_phone_lines', updated.filter(l => l.label || l.number));
                }}
                placeholder="e.g., Repeats"
                style={{ flex: 0.4 }}
              />
              <TextField
                label="Number"
                size="small"
                value={line.number || ''}
                onChange={(e) => {
                  const updated = [...(settings.pharmacy_phone_lines || [])];
                  updated[idx] = { ...line, number: e.target.value };
                  onUpdate('pharmacy_phone_lines', updated.filter(l => l.label || l.number));
                }}
                placeholder="Phone number"
                style={{ flex: 0.6 }}
              />
            </div>
          );
        })}
      </div>

      {/* Shortcut Buttons */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Shortcut Buttons (up to 4)</h5>
        {[0, 1, 2, 3].map((idx) => {
          const button = (settings.buttons || [])[idx] || { label: '', url: '' };
          return (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <TextField
                label="Label"
                size="small"
                value={button.label || ''}
                onChange={(e) => {
                  const updated = [...(settings.buttons || [])];
                  updated[idx] = { ...button, label: e.target.value };
                  onUpdate('buttons', updated.filter(b => b.label || b.url));
                }}
                placeholder="e.g., NHS 111"
                style={{ flex: 0.3 }}
              />
              <TextField
                label="URL"
                size="small"
                value={button.url || ''}
                onChange={(e) => {
                  const updated = [...(settings.buttons || [])];
                  updated[idx] = { ...button, url: e.target.value };
                  onUpdate('buttons', updated.filter(b => b.label || b.url));
                }}
                placeholder="https://..."
                style={{ flex: 0.7 }}
              />
            </div>
          );
        })}
      </div>

      {/* Online Shop */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Online Shop</h5>
        <FormControlLabel
          control={
            <Checkbox
              checked={settings.shop_enabled || false}
              onChange={(e) => onUpdate('shop_enabled', e.target.checked)}
            />
          }
          label="Show online shop"
        />
        {settings.shop_enabled && (
          <>
            <InputLabel sx={{ mt: 2 }}>Pharmacy EPOS shopId</InputLabel>
            <Input
              value={settings.shop_id || ''}
              onChange={(e) => onUpdate('shop_id', e.target.value)}
              placeholder="UUID"
              fullWidth
            />
          </>
        )}
      </div>

      {/* Pharmacy News */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Pharmacy News</h5>
        <InputLabel>Top box text (news/updates)</InputLabel>
        <TextField
          multiline
          rows={3}
          value={settings.top_box_text || ''}
          onChange={(e) => onUpdate('top_box_text', e.target.value)}
          placeholder="Important news displayed at top of app..."
          fullWidth
          style={{ marginTop: 8 }}
        />
      </div>
    </div>
  );
};

export const panelStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: 20,
  backgroundColor: "#fff",
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
};

export const panelHeaderStyle = {
  fontSize: 15,
  fontWeight: 700,
  color: "#111827",
  marginBottom: 16,
  paddingBottom: 10,
  borderBottom: "1px solid #f0f1f3",
};

export const reportListStyle = {
  display: "flex",
  flexDirection: "column",
};

const reportRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "6px 0",
  borderTop: "1px solid #f5f6f7",
};

export const ReportRow = ({ checked, onChange, label, trailing = null }) => (
  <div style={reportRowStyle}>
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={onChange} size="small" />}
      label={label}
      style={{ margin: 0 }}
    />
    {trailing}
  </div>
);


// ---------------------------------------------------------------------------
// ORGANISATION SETTINGS — group manager address and the scheduled report opt-ins.
//
// `values`   { manager_email, report_sim, report_expiring, report_slow,
//              report_fridge, send_expired, send_timesheets, timesheet_day,
//              payroll_month_end }
// `onChange` (key, value) for any single field
// `extraRows` appended to the bottom of the report list — used by settings.js
//              for the two toggles whose state lives behind their own
//              token-scoped endpoints (monthly site review, locum payment
//              email) and so cannot be driven for another organisation.
// ---------------------------------------------------------------------------
export const OrganisationReportsPanel = ({ values = {}, onChange, extraRows = null }) => {
  const v = (k, fallback = false) => (values[k] === undefined ? fallback : values[k]);
  const toggle = (k) => () => onChange(k, !v(k));

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>Group manager</div>

      <Input
        type="email"
        fullWidth
        value={v("manager_email", "")}
        onChange={(e) => onChange("manager_email", e.target.value)}
        placeholder="Group manager email"
        style={{ marginBottom: 16 }}
      />

      <div style={reportListStyle}>
        <ReportRow checked={v("report_sim")} onChange={toggle("report_sim")} label="SIM activity" />
        <ReportRow checked={v("report_expiring")} onChange={toggle("report_expiring")} label="Expiring items" />
        <ReportRow checked={v("report_slow")} onChange={toggle("report_slow")} label="Slow movers" />
        <ReportRow checked={v("report_fridge")} onChange={toggle("report_fridge")} label="Fridge checks" />
        <ReportRow
          checked={v("send_expired")}
          onChange={toggle("send_expired")}
          label="Send monthly removed/expired drugs report"
        />
        {/* A dropdown, not a number box, and the same one as Payroll month end
            below. "Last day of the month" cannot be a number - the last day is
            the 28th, 29th, 30th or 31st depending on the month - and a fixed
            29/30/31 used to mean the timesheet was simply never sent in the
            short months, with nothing to show it had been missed. */}
        <ReportRow
          checked={v("send_timesheets")}
          onChange={toggle("send_timesheets")}
          label="Send monthly timesheets"
          trailing={
            <select
              value={String(v("timesheet_day", "last"))}
              onChange={(e) => onChange("timesheet_day", e.target.value)}
              style={{ fontSize: 13, padding: "4px 8px" }}
            >
              <option value="last">Last day of the month</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={String(d)}>{d}</option>
              ))}
            </select>
          }
        />
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 6px 30px", fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>Payroll month end:</span>
          <select
            value={String(v("payroll_month_end", "last"))}
            onChange={(e) => onChange("payroll_month_end", e.target.value)}
            style={{ fontSize: 13, padding: "4px 8px" }}
          >
            <option value="last">Last day of the month</option>
            <option value="first">First day of the month</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={String(d)}>{d}</option>
            ))}
          </select>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", margin: "0 0 6px 30px" }}>
          Each timesheet covers the payroll month ending on this date (running
          backwards from it). Days between the generation day and the payroll
          month end are filled with the staff member's scheduled hours, shown
          in italics on an amber background as ESTIMATED — so the submitted
          totals are right for the payment cycle unless someone doesn't turn
          up. Each spreadsheet also opens with a reconciliation page showing
          the actual clockings for the previously estimated days.
        </div>

        {extraRows}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// CLOCK TOLERANCES — grace period and ignored late departure.
//
// Two organisation-wide numbers, held server-side in clock_settings (NOT in
// the group settings document), read and written by the head-office clock
// endpoints. They change how the clock grid READS a day; they never change a
// recorded clocking. A day is only rewritten when somebody presses Apply on
// the month view, and that writes its reasoning into the day's own log.
//
// `values`   { grace_mins, late_leave_mins }
// `onChange` (key, value)
// ---------------------------------------------------------------------------
export const ClockTolerancesPanel = ({ values = {}, onChange, disabled = false, note = null }) => {
  const num = (k, fallback = 0) => (values[k] === undefined || values[k] === null ? fallback : values[k]);
  const box = {
    fontSize: 13, padding: "6px 8px", border: "1px solid #9ca3af", borderRadius: 4,
    width: 70, marginRight: 6,
  };
  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>Clock tolerances</div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <input type="number" min={0} max={30} disabled={disabled} style={box}
          value={num("grace_mins")}
          onChange={(e) => onChange("grace_mins", e.target.value)} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>Grace period (minutes, 0 to 30)</span>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px 0", lineHeight: 1.45 }}>
        A clock in or clock out within this many minutes of the scheduled time, either side,
        is counted as happening at the scheduled time, and the day's hours are worked out
        from the schedule. A day that is otherwise clean then completes on its own instead
        of waiting for a manager. Set to 0 to read every day exactly as clocked.
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <input type="number" min={0} max={60} disabled={disabled} style={box}
          value={num("late_leave_mins")}
          onChange={(e) => onChange("late_leave_mins", e.target.value)} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>Ignore late departure up to (minutes, 0 to 60)</span>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", margin: "0 0 10px 0", lineHeight: 1.45 }}>
        Staying later than the scheduled finish by up to this many minutes counts as finishing
        on time, so it is not put to a manager as overtime to approve. It only ever forgives
        leaving LATE — leaving early is still shown.
      </div>

      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.45 }}>
        Neither setting alters a recorded clocking. They change what the clock screens
        calculate; the hours that reach payroll are only rewritten when somebody presses
        "Apply grace period" on the month view, and every day that touches gets a log entry
        saying which clocking was moved, by how much, and under which tolerance.
      </div>
      {note}
    </div>
  );
};


// ---------------------------------------------------------------------------
// ORGANISATION IDENTITY + LOGIN — name, username, password.
// ---------------------------------------------------------------------------
export const OrganisationIdentityPanel = ({
  organisationName = "",
  username = "",
  password = "",
  disabled = false,
  showPassword = false,
  onToggleShowPassword,
  onChange,
  note = null,
}) => (
  <div style={panelStyle}>
    <div style={panelHeaderStyle}>Organisation and login</div>
    {note}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
      <div>
        <InputLabel shrink>Organisation name</InputLabel>
        <Input fullWidth value={organisationName} disabled={disabled}
               onChange={(e) => onChange("organisation_name", e.target.value)} />
      </div>
      <div>
        <InputLabel shrink>Username</InputLabel>
        <Input fullWidth value={username} disabled={disabled}
               onChange={(e) => onChange("email", e.target.value)} />
      </div>
      <div>
        <InputLabel shrink>Password</InputLabel>
        <Input fullWidth type={showPassword ? "text" : "password"} value={password} disabled={disabled}
               onChange={(e) => onChange("password", e.target.value)} />
        <FormControlLabel
          control={<Checkbox size="small" checked={showPassword}
                             onChange={(e) => onToggleShowPassword(e.target.checked)} />}
          label={<span style={{ fontSize: 12 }}>Show password</span>}
        />
      </div>
    </div>
  </div>
);


// ---------------------------------------------------------------------------
// SITE SETTINGS — everything that belongs to ONE branch: opening times, OCS
// code and the patient app configuration.
//
// `member`   the selected members[] entry
// `onChange` (key, value) where key is openingTimes | ocsCode | patientAppSettings
// `extraAfterOcs` slot under the OCS field — settings.js puts the appraisal
//              widget key panel here (it is keyed to the logged-in org's token,
//              so it has no meaning when administering another organisation).
// ---------------------------------------------------------------------------
export const BranchSettingsPanel = ({ member, onChange, extraAfterOcs = null, onInheritPatientApp }) => {
  if (!member) {
    return <div style={{ padding: 14, fontSize: 13, color: "#6b7280" }}>Select a branch to edit its settings.</div>;
  }

  const openingTimes = member.openingTimes || defaultOpeningTimes;
  const patientAppSettings = member.patientAppSettings || defaultPatientAppSettings;

  const updateOpeningTime = (day, field, value) =>
    onChange("openingTimes", { ...openingTimes, [day]: { ...openingTimes[day], [field]: value } });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h4>Opening times</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {Object.keys(defaultOpeningTimes).map((day) => (
            <div key={day} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{day}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <TextField
                  label="Open"
                  size="small"
                  fullWidth
                  value={openingTimes[day]?.open || ""}
                  onChange={(e) => updateOpeningTime(day, "open", e.target.value)}
                />
                <TextField
                  label="Close"
                  size="small"
                  fullWidth
                  value={openingTimes[day]?.close || ""}
                  onChange={(e) => updateOpeningTime(day, "close", e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <InputLabel>OCS Code</InputLabel>
          <Input fullWidth value={member.ocsCode || ""}
                 onChange={(e) => onChange("ocsCode", e.target.value)} />
        </div>

        {extraAfterOcs}
      </div>

      <PatientAppSection
        settings={patientAppSettings}
        onUpdate={(key, value) => onChange("patientAppSettings", { ...patientAppSettings, [key]: value })}
        onInherit={onInheritPatientApp}
      />
    </div>
  );
};

